/**
 * Um seletor com busca, no feitio do filtro de planilha.
 *
 * Existe porque um `<select>` nativo deixa de servir quando a lista cresce:
 * as causas de glosa já são seis com um arquivo e serão dezenas com vários,
 * e procurar "9300" rolando uma lista é trabalho que o computador faz melhor.
 *
 * Duas decisões de comportamento valem registro:
 *
 * - **A busca casa em código E em texto.** Quem audita glosa às vezes lembra
 *   o número da causa e às vezes a frase; obrigar a lembrar justo o que o
 *   campo indexa seria arbitrário.
 * - **A opção "todas" nunca é filtrada pela busca.** Ela é a saída, não um
 *   item da lista — e perdê-la ao digitar deixaria a pessoa presa num
 *   recorte sem jeito óbvio de desfazer.
 *
 * O painel é `position: fixed`, com as coordenadas calculadas no momento de
 * abrir. Um painel `absolute` é **recortado** pelo `overflow: hidden` do
 * `.ff-painel` que o contém — foi exatamente o que aconteceu na primeira
 * versão, e é o mesmo motivo pelo qual o componente `Ajuda` já fazia assim.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function SelecaoBuscavel({
  rotulo,
  valor,
  opcoes,
  aoMudar,
  textoTodas = 'Todas',
  largura = 200,
  ajuda = null,
  placeholder = 'Buscar…',
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, largura: 260, altura: 320 });
  const caixa = useRef(null);
  const gatilho = useRef(null);
  const campo = useRef(null);

  const posicionar = useCallback(() => {
    const el = gatilho.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margem = 12;
    // Nunca mais estreito que o gatilho, para o painel não parecer solto, e
    // nunca abaixo de 300: o motivo da glosa é uma frase inteira, e num
    // painel estreito ela quebra em três linhas por item. Nunca mais largo
    // que a janela, para não vazar no celular.
    const largura = Math.min(
      Math.max(r.width, 300),
      window.innerWidth - margem * 2
    );
    const left = Math.max(
      margem,
      Math.min(r.left, window.innerWidth - largura - margem)
    );

    // Abre para baixo; se não couber, abre para cima. E a altura se ajusta
    // ao que sobrou, em vez de o painel ser cortado pela borda da janela.
    const abaixo = window.innerHeight - r.bottom;
    const acima = r.top;
    const paraBaixo = abaixo >= 220 || abaixo >= acima;
    const disponivel = (paraBaixo ? abaixo : acima) - margem - 8;

    setPos({
      top: paraBaixo ? r.bottom + 6 : undefined,
      bottom: paraBaixo ? undefined : window.innerHeight - r.top + 6,
      left,
      largura,
      altura: Math.max(180, Math.min(340, disponivel)),
    });
  }, []);

  // Fecha ao clicar fora. Sem isto, abrir um filtro e clicar em outro
  // deixaria os dois abertos, sobrepostos.
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => {
      if (caixa.current && !caixa.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  useEffect(() => {
    if (aberto && campo.current) campo.current.focus();
    if (!aberto) setBusca('');
  }, [aberto]);

  // Rolar a página com o painel aberto o deixaria para trás: ele é fixo, o
  // gatilho não.
  useEffect(() => {
    if (!aberto) return undefined;
    const refazer = () => posicionar();
    window.addEventListener('scroll', refazer, true);
    window.addEventListener('resize', refazer);
    return () => {
      window.removeEventListener('scroll', refazer, true);
      window.removeEventListener('resize', refazer);
    };
  }, [aberto, posicionar]);

  const filtradas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return opcoes;
    return opcoes.filter(
      (o) =>
        String(o.valor).toLowerCase().includes(t) ||
        String(o.texto).toLowerCase().includes(t)
    );
  }, [opcoes, busca]);

  const selecionada = opcoes.find((o) => o.valor === valor);

  return (
    <div style={{ position: 'relative', minWidth: largura }} ref={caixa}>
      <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
        {rotulo}
        {ajuda}
      </label>

      <button
        ref={gatilho}
        type="button"
        className="form-select form-select-sm d-flex align-items-center justify-content-between"
        style={{
          borderRadius: 8,
          textAlign: 'left',
          background: valor ? 'var(--ff-fundo-suave, #f1f5f9)' : undefined,
          fontWeight: valor ? 600 : 400,
        }}
        onClick={() => {
          posicionar();
          setAberto((a) => !a);
        }}
        aria-expanded={aberto}
        aria-haspopup="listbox"
        title={selecionada ? selecionada.texto : textoTodas}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selecionada ? selecionada.texto : textoTodas}
        </span>
      </button>

      {aberto && (
        <div
          role="listbox"
          style={{
            position: 'fixed',
            zIndex: 1080,
            top: pos.top,
            bottom: pos.bottom,
            left: pos.left,
            width: pos.largura,
            background: 'var(--ff-surface, #fff)',
            border: '1px solid var(--ff-border)',
            borderRadius: 10,
            boxShadow: '0 12px 28px rgba(16,24,40,.14)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ padding: 8, borderBottom: '1px solid var(--ff-border-soft)' }}>
            <input
              ref={campo}
              className="form-control form-control-sm"
              style={{ borderRadius: 8 }}
              placeholder={placeholder}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setAberto(false);
                // Enter escolhe a única que sobrou: com a lista reduzida a
                // um item, pedir o clique é um passo a mais sem ganho.
                if (e.key === 'Enter' && filtradas.length === 1) {
                  aoMudar(filtradas[0].valor);
                  setAberto(false);
                }
              }}
            />
          </div>

          <div style={{ maxHeight: pos.altura, overflowY: 'auto', flex: 1 }}>
            <button
              type="button"
              className="dropdown-item"
              style={{
                padding: '7px 12px',
                fontSize: 13,
                fontWeight: valor ? 400 : 600,
                border: 0,
                background: 'none',
                width: '100%',
                textAlign: 'left',
              }}
              onClick={() => {
                aoMudar('');
                setAberto(false);
              }}
            >
              {textoTodas}
            </button>

            {filtradas.length === 0 ? (
              <div
                style={{ padding: '10px 12px', fontSize: 13, color: 'var(--ff-muted)' }}
              >
                Nada encontrado neste recorte.
              </div>
            ) : (
              filtradas.map((o) => (
                <button
                  key={o.valor}
                  type="button"
                  className="dropdown-item"
                  style={{
                    padding: '7px 12px',
                    fontSize: 13,
                    border: 0,
                    background: o.valor === valor ? 'var(--ff-fundo-suave, #f1f5f9)' : 'none',
                    fontWeight: o.valor === valor ? 600 : 400,
                    width: '100%',
                    textAlign: 'left',
                    whiteSpace: 'normal',
                  }}
                  onClick={() => {
                    aoMudar(o.valor);
                    setAberto(false);
                  }}
                >
                  {o.texto}
                </button>
              ))
            )}
          </div>

          {opcoes.length > 0 && (
            <div
              style={{
                padding: '6px 12px',
                fontSize: 11,
                color: 'var(--ff-muted)',
                borderTop: '1px solid var(--ff-border-soft)',
              }}
            >
              {/* O contador diz que a lista é do RECORTE, não do universo —
                  senão a pessoa procura um valor que existe no sistema, não
                  acha, e conclui que o filtro está quebrado. */}
              {filtradas.length} de {opcoes.length} neste recorte
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SelecaoBuscavel;
